import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useModalStackStore } from '@/stores/modalStackStore';
import { useConfigStore } from '@/stores/configStore';
import { useHomeStore } from '@/stores/homeStore';
import { useTranslation } from 'react-i18next';
import { PATHS } from '@/router/paths';
import { clearRegionTransitionHistory, recordRegionTransition } from '@/utils/regionTransitionHistory';

interface NavigationConfig {
    speak: (text: string) => void;
    isHorizontal?: boolean;
    gnbSelector?: string;
    mainContentSelector?: string;
    footerSelector?: string;
    zoomSelector?: string;
    regionSelector?: string;
}

export const useRegionNavigation = ({
    speak,
    isHorizontal = true,
    gnbSelector = '[data-nav="gnb"]',
    mainContentSelector = '[data-nav="content"]',
    footerSelector = '[data-nav="footer"]',
    zoomSelector = '[data-nav="zoom"]',
    regionSelector = '[data-region-nav="true"]',
}: NavigationConfig) => {
    const voice = useConfigStore((state) => state.config?.tts?.voice);
    const { t } = useTranslation();
    const location = useLocation();

    useEffect(() => {
        clearRegionTransitionHistory();
    }, [location.pathname]);

    useEffect(() => {
        clearRegionTransitionHistory();
    }, [isHorizontal]);

    useEffect(() => {
        const initRegionNavigation = () => {
            // Get main navigation elements
            const gnb = document.querySelector(gnbSelector) as HTMLElement;
            const mainContent = document.querySelector(mainContentSelector) as HTMLElement;
            const footer = document.querySelector(footerSelector) as HTMLElement;

            // Set tabindex for main navigation areas
            [gnb, mainContent, footer].forEach((el) => {
                if (el) el.setAttribute('tabindex', '0');
            });

            // Helper function to get zoom element dynamically (since it may appear/disappear)
            const getZoomElement = (): HTMLElement | null => {
                const zoom = document.querySelector(zoomSelector) as HTMLElement;
                if (zoom && !zoom.hasAttribute('tabindex')) {
                    zoom.setAttribute('tabindex', '0');
                }
                return zoom;
            };

            // Function to get regions within a container
            const getRegionsInContainer = (container: HTMLElement): HTMLElement[] => {
                if (!container) return [];
                const regions = container.querySelectorAll(regionSelector);
                const visibleRegions = Array.from(regions).filter((el) => {
                    const htmlEl = el as HTMLElement;
                    // Check if element is visible and has data-region-nav="true"
                    const isVisible = !!(htmlEl.offsetWidth || htmlEl.offsetHeight || htmlEl.getClientRects().length);
                    const regionNavValue = htmlEl.getAttribute('data-region-nav');
                    const isRegionNav = regionNavValue === 'true' || regionNavValue === '';
                    return isVisible && isRegionNav;
                }) as HTMLElement[];

                // Sort by region-order attribute if available, otherwise by DOM order
                return visibleRegions.sort((a, b) => {
                    const orderA = parseInt(a.getAttribute('data-region-order') || '0', 10);
                    const orderB = parseInt(b.getAttribute('data-region-order') || '0', 10);

                    if (orderA !== orderB) {
                        return orderA - orderB;
                    }

                    // Fallback to DOM order if no region-order or same order
                    const position = a.compareDocumentPosition(b);
                    return position & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
                });
            };

            // Function to get current navigation context
            const getCurrentNavigationContext = (
                focusedElement: HTMLElement
            ): {
                area: 'gnb' | 'content' | 'footer' | 'zoom' | 'unknown';
                isInRegion: boolean;
                isInParent: boolean;
                regionIndex: number;
                availableRegions: HTMLElement[];
                parentElement: HTMLElement | null;
                hasRegionNav: boolean;
            } => {
                let area: 'gnb' | 'content' | 'footer' | 'zoom' | 'unknown' = 'unknown';
                let availableRegions: HTMLElement[] = [];
                let isInRegion = false;
                let isInParent = false;
                let regionIndex = -1;
                let parentElement: HTMLElement | null = null;
                let hasRegionNav = false;

                // Check if current element has data-region-nav="true"
                hasRegionNav = focusedElement.getAttribute('data-region-nav') === 'true';

                const zoom = getZoomElement(); // Get zoom dynamically

                if (gnb && (focusedElement === gnb || gnb.contains(focusedElement))) {
                    area = 'gnb';
                    parentElement = gnb;
                    isInParent = focusedElement === gnb;
                    availableRegions = getRegionsInContainer(gnb);

                    // Check if focused element is a region or inside a region
                    for (let i = 0; i < availableRegions.length; i++) {
                        if (availableRegions[i] === focusedElement || availableRegions[i].contains(focusedElement)) {
                            isInRegion = true;
                            regionIndex = i;
                            break;
                        }
                    }

                    // Nếu chưa tìm thấy, kiểm tra xem element có data-region-order giống region nào không
                    if (!isInRegion) {
                        const elementOrder = focusedElement.getAttribute('data-region-order');
                        if (elementOrder) {
                            for (let i = 0; i < availableRegions.length; i++) {
                                const regionOrder = availableRegions[i].getAttribute('data-region-order');
                                if (regionOrder === elementOrder) {
                                    isInRegion = true;
                                    regionIndex = i;
                                    break;
                                }
                            }
                        }
                    }
                } else if (mainContent && (focusedElement === mainContent || mainContent.contains(focusedElement))) {
                    area = 'content';
                    parentElement = mainContent;
                    isInParent = focusedElement === mainContent;
                    availableRegions = getRegionsInContainer(mainContent);

                    // Check if focused element is a region or inside a region
                    for (let i = 0; i < availableRegions.length; i++) {
                        if (availableRegions[i] === focusedElement || availableRegions[i].contains(focusedElement)) {
                            isInRegion = true;
                            regionIndex = i;
                            break;
                        }
                    }

                    // Nếu chưa tìm thấy, kiểm tra xem element có data-region-order giống region nào không
                    if (!isInRegion) {
                        const elementOrder = focusedElement.getAttribute('data-region-order');
                        if (elementOrder) {
                            for (let i = 0; i < availableRegions.length; i++) {
                                const regionOrder = availableRegions[i].getAttribute('data-region-order');
                                if (regionOrder === elementOrder) {
                                    isInRegion = true;
                                    regionIndex = i;
                                    break;
                                }
                            }
                        }
                    }
                } else if (footer && (focusedElement === footer || footer.contains(focusedElement))) {
                    area = 'footer';
                    parentElement = footer;
                    isInParent = focusedElement === footer;
                    availableRegions = getRegionsInContainer(footer);

                    // Check if focused element is a region or inside a region
                    for (let i = 0; i < availableRegions.length; i++) {
                        if (availableRegions[i] === focusedElement || availableRegions[i].contains(focusedElement)) {
                            isInRegion = true;
                            regionIndex = i;
                            break;
                        }
                    }

                    // Nếu chưa tìm thấy, kiểm tra xem element có data-region-order giống region nào không
                    if (!isInRegion) {
                        const elementOrder = focusedElement.getAttribute('data-region-order');
                        if (elementOrder) {
                            for (let i = 0; i < availableRegions.length; i++) {
                                const regionOrder = availableRegions[i].getAttribute('data-region-order');
                                if (regionOrder === elementOrder) {
                                    isInRegion = true;
                                    regionIndex = i;
                                    break;
                                }
                            }
                        }
                    }
                } else if (zoom && (focusedElement === zoom || zoom.contains(focusedElement))) {
                    area = 'zoom';
                    parentElement = zoom;
                    isInParent = focusedElement === zoom;
                    availableRegions = getRegionsInContainer(zoom);

                    // Check if focused element is a region or inside a region
                    for (let i = 0; i < availableRegions.length; i++) {
                        if (availableRegions[i] === focusedElement || availableRegions[i].contains(focusedElement)) {
                            isInRegion = true;
                            regionIndex = i;
                            break;
                        }
                    }

                    // Nếu chưa tìm thấy, kiểm tra xem element có data-region-order giống region nào không
                    if (!isInRegion) {
                        const elementOrder = focusedElement.getAttribute('data-region-order');
                        if (elementOrder) {
                            for (let i = 0; i < availableRegions.length; i++) {
                                const regionOrder = availableRegions[i].getAttribute('data-region-order');
                                if (regionOrder === elementOrder) {
                                    isInRegion = true;
                                    regionIndex = i;
                                    break;
                                }
                            }
                        }
                    }
                }

                return { area, isInRegion, isInParent, regionIndex, availableRegions, parentElement, hasRegionNav };
            };

            // Hàm tìm div cha có data-region-nav=true chứa element hiện tại
            // Hoặc tìm region có cùng data-region-order với element
            const findParentRegion = (element: HTMLElement, availableRegions: HTMLElement[]): HTMLElement | null => {
                // Trước tiên kiểm tra xem element có nằm trong region nào không
                for (const region of availableRegions) {
                    if (region === element || region.contains(element)) {
                        return region;
                    }
                }

                // Nếu element không nằm trong region nào, tìm xem element có data-region-order không
                let elementOrder = element.getAttribute('data-region-order');
                
                // Nếu element không có data-region-order, tìm từ parent elements
                if (!elementOrder) {
                    let current: HTMLElement | null = element;
                    while (current && current.parentElement) {
                        const order = current.getAttribute('data-region-order');
                        if (order) {
                            elementOrder = order;
                            break;
                        }
                        current = current.parentElement;
                    }
                }

                // Nếu tìm thấy order, tìm region có cùng order
                if (elementOrder) {
                    for (const region of availableRegions) {
                        const regionOrder = region.getAttribute('data-region-order');
                        if (regionOrder === elementOrder) {
                            return region;
                        }
                    }
                }

                return null;
            };

            // Hàm tìm focusable element đầu tiên trong một region
            const findFirstFocusableInRegion = (region: HTMLElement): HTMLElement | null => {
                const FOCUSABLE_ELEMENTS = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

                // Tìm tất cả focusable elements bên trong region
                const focusableElements = Array.from(region.querySelectorAll(FOCUSABLE_ELEMENTS)).filter((el) => {
                    const htmlEl = el as HTMLElement;
                    const tabindex = htmlEl.getAttribute('tabindex');
                    const isVisible = !!(htmlEl.offsetWidth || htmlEl.offsetHeight || htmlEl.getClientRects().length);
                    return tabindex !== '-1' && isVisible;
                }) as HTMLElement[];

                if (focusableElements.length > 0) {
                    return focusableElements[0];
                }

                // Nếu không tìm thấy focusable element bên trong, kiểm tra chính region
                const matchesFocusable = region.matches(FOCUSABLE_ELEMENTS);
                const regionTabindex = region.getAttribute('tabindex') || '0';
                const isVisible = !!(region.offsetWidth || region.offsetHeight || region.getClientRects().length);

                if (matchesFocusable && regionTabindex !== '-1' && isVisible) {
                    return region;
                }

                return null;
            };

            // Hàm tìm region tiếp theo/trước đó và focus vào focusable element đầu tiên của nó
            const navigateToAdjacentRegion = (currentElement: HTMLElement, availableRegions: HTMLElement[], direction: 'up' | 'down'): HTMLElement | null => {
                if (availableRegions.length === 0) return null;

                // Tìm region cha chứa current element
                const currentRegion = findParentRegion(currentElement, availableRegions);
                if (!currentRegion) return null;

                // Tìm index của current region
                const currentIndex = availableRegions.indexOf(currentRegion);
                if (currentIndex === -1) return null;

                // Tính toán next/prev region
                let targetRegion: HTMLElement | null = null;
                if (direction === 'down') {
                    targetRegion = currentIndex + 1 < availableRegions.length ? availableRegions[currentIndex + 1] : null;
                } else {
                    targetRegion = currentIndex - 1 >= 0 ? availableRegions[currentIndex - 1] : null;
                }

                if (!targetRegion) return null;

                // Tìm focusable element đầu tiên trong target region
                return findFirstFocusableInRegion(targetRegion);
            };

            // Function to focus and announce element
            const focusAndAnnounce = (element: HTMLElement, customText?: string, shouldSpeak: boolean = true) => {
                element.focus();
                const text = customText || element.getAttribute('aria-label') || element.textContent?.trim() || '영역';

                if (voice && shouldSpeak) {
                    speak(text);
                }
            };

            const isScreenSaverHomeMenu = () => {
                const isScreenSaverFullscreen = useConfigStore.getState().config?.ui?.isScreenSaverFullscreen;
                const isHomeMenuVisible = useHomeStore.getState().isHomeMenuVisible;
                return location.pathname === PATHS.HOME.INDEX && !!isScreenSaverFullscreen && isHomeMenuVisible;
            };

            const wrapScreenSaverHomeRegion = (availableRegions: HTMLElement[], direction: 'up' | 'down'): HTMLElement | null => {
                if (availableRegions.length === 0) return null;

                const targetRegion =
                    direction === 'down' ? availableRegions[0] : availableRegions[availableRegions.length - 1];

                return findFirstFocusableInRegion(targetRegion);
            };

            // MutationObserver to watch for content changes
            const observer = new MutationObserver((mutations) => {
                let shouldRescan = false;
                mutations.forEach((mutation) => {
                    if (mutation.type === 'childList') {
                        const addedNodes = Array.from(mutation.addedNodes);
                        const removedNodes = Array.from(mutation.removedNodes);

                        [...addedNodes, ...removedNodes].forEach((node) => {
                            if (node.nodeType === Node.ELEMENT_NODE) {
                                const element = node as Element;
                                if (
                                    element.matches?.(regionSelector) ||
                                    element.querySelector?.(regionSelector) ||
                                    element.closest?.(mainContentSelector) ||
                                    element.closest?.(footerSelector) ||
                                    element.closest?.(zoomSelector) ||
                                    element.matches?.(zoomSelector)
                                ) {
                                    shouldRescan = true;
                                }
                            }
                        });
                    }
                });

                if (shouldRescan) {
                    // setupAllRegions();
                }
            });

            // Observe changes in main content, footer, and document body for zoom element
            if (mainContent) {
                observer.observe(mainContent, { childList: true, subtree: true });
            }
            if (footer) {
                observer.observe(footer, { childList: true, subtree: true });
            }
            // Observe body to detect when zoom element is added/removed
            observer.observe(document.body, { childList: true, subtree: true });

            const handleKeyDown = (e: KeyboardEvent) => {
                if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;

                // Check if the focused element is part of a dialog or modal (focus trap area)
                const target = e.target as HTMLElement;
                const isInDialog = target.closest('[role="dialog"], [role="alertdialog"], .modal, .dialog, [data-focus-trap]');
                if (isInDialog) {
                    return;
                }

                // Block ArrowUp and ArrowDown when a modal that blocks global keypad is open
                if (useModalStackStore.getState().blocksGlobalKeypad()) {
                    e.preventDefault();
                    return;
                }

                e.preventDefault();

                const focusedElement = document.activeElement as HTMLElement;
                const context = getCurrentNavigationContext(focusedElement);
                const zoom = getZoomElement(); // Get zoom dynamically for navigation
                const isHomePage = location.pathname === PATHS.HOME.INDEX;
                const useMainRegionNavOrder = !isHomePage;

                const focusAreaAbove = (from: 'gnb' | 'content' | 'footer') => {
                    if (!useMainRegionNavOrder) return false;

                    if (isHorizontal) {
                        // ArrowUp (ngang): Main Menu → Footer → Content → (wrap Main Menu)
                        if (from === 'gnb' && footer) {
                            recordRegionTransition('gnb', 'footer');
                            focusAndAnnounce(footer, t('region.footer'));
                            return true;
                        }
                        if (from === 'footer' && mainContent) {
                            recordRegionTransition('footer', 'content');
                            focusAndAnnounce(mainContent, t('region.content'));
                            return true;
                        }
                        if (from === 'content' && gnb) {
                            recordRegionTransition('content', 'gnb');
                            focusAndAnnounce(gnb, t('region.gnb'));
                            return true;
                        }
                        return false;
                    }

                    // ArrowUp (dọc): Footer → Main Menu → Content → (wrap Footer)
                    if (from === 'footer' && gnb) {
                        recordRegionTransition('footer', 'gnb');
                        focusAndAnnounce(gnb, t('region.gnb'));
                        return true;
                    }
                    if (from === 'gnb' && mainContent) {
                        recordRegionTransition('gnb', 'content');
                        focusAndAnnounce(mainContent, t('region.content'));
                        return true;
                    }
                    if (from === 'content' && footer) {
                        recordRegionTransition('content', 'footer');
                        focusAndAnnounce(footer, t('region.footer'));
                        return true;
                    }
                    return false;
                };

                const focusAreaBelow = (from: 'gnb' | 'content' | 'footer') => {
                    if (!useMainRegionNavOrder) return false;

                    if (isHorizontal) {
                        // ArrowDown (ngang): Content → Footer → Main Menu → (wrap Content)
                        if (from === 'content' && footer) {
                            recordRegionTransition('content', 'footer');
                            focusAndAnnounce(footer, t('region.footer'));
                            return true;
                        }
                        if (from === 'footer' && gnb) {
                            recordRegionTransition('footer', 'gnb');
                            focusAndAnnounce(gnb, t('region.gnb'));
                            return true;
                        }
                        if (from === 'gnb' && mainContent) {
                            recordRegionTransition('gnb', 'content');
                            focusAndAnnounce(mainContent, t('region.content'));
                            return true;
                        }
                        return false;
                    }

                    // ArrowDown (dọc): Content → Main Menu → Footer → (wrap Content)
                    if (from === 'content' && gnb) {
                        recordRegionTransition('content', 'gnb');
                        focusAndAnnounce(gnb, t('region.gnb'));
                        return true;
                    }
                    if (from === 'gnb' && footer) {
                        recordRegionTransition('gnb', 'footer');
                        focusAndAnnounce(footer, t('region.footer'));
                        return true;
                    }
                    if (from === 'footer' && mainContent) {
                        recordRegionTransition('footer', 'content');
                        focusAndAnnounce(mainContent, t('region.content'));
                        return true;
                    }
                    return false;
                };

                if (e.key === 'ArrowUp') {
                    switch (context.area) {
                        case 'gnb':
                            if (context.isInParent) {
                                if (focusAreaAbove('gnb')) {
                                    return;
                                }
                                if (zoom) {
                                    focusAndAnnounce(zoom, t('region.zoom'));
                                } else if (footer) {
                                    focusAndAnnounce(footer, t('region.footer'));
                                }
                                return;
                            }
                            if (context.isInRegion) {
                                // Đang ở trong region -> tìm focusable element trong region trước đó
                                const targetElement = navigateToAdjacentRegion(focusedElement, context.availableRegions, 'up');
                                if (targetElement) {
                                    focusAndAnnounce(targetElement, undefined, false);
                                } else if (focusAreaAbove('gnb')) {
                                    // vertical: gnb -> content
                                } else {
                                    // Không có region trước đó -> chuyển qua zoom hoặc footer
                                    if (zoom) {
                                        focusAndAnnounce(zoom, t('region.zoom'));
                                    } else if (footer) {
                                        focusAndAnnounce(footer, t('region.footer'));
                                    }
                                }
                                return;
                            } else if (focusAreaAbove('gnb')) {
                                // vertical: gnb -> content
                                return;
                            } else {
                                // Không ở trong region -> chuyển qua zoom hoặc footer
                                if (zoom) {
                                    focusAndAnnounce(zoom, t('region.zoom'));
                                } else if (footer) {
                                    focusAndAnnounce(footer, t('region.footer'));
                                }
                                return;
                            }
                            break;
                        case 'content':
                            if (context.isInParent) {
                                if (focusAreaAbove('content')) {
                                    return;
                                }
                                if (isHomePage && footer) {
                                    focusAndAnnounce(footer, t('region.footer'));
                                } else if (gnb) {
                                    focusAndAnnounce(gnb, t('region.gnb'));
                                }
                                return;
                            }
                            if (context.isInRegion) {
                                // Đang ở trong region -> tìm focusable element trong region trước đó
                                const targetElement = navigateToAdjacentRegion(focusedElement, context.availableRegions, 'up');
                                if (targetElement) {
                                    focusAndAnnounce(targetElement, undefined, false);
                                } else if (isScreenSaverHomeMenu()) {
                                    const wrapped = wrapScreenSaverHomeRegion(context.availableRegions, 'up');
                                    if (wrapped) focusAndAnnounce(wrapped, undefined, false);
                                } else if (focusAreaAbove('content')) {
                                    // vertical: content -> footer (wrap)
                                } else if (isHomePage && footer) {
                                    focusAndAnnounce(footer, t('region.footer'));
                                } else if (gnb) {
                                    focusAndAnnounce(gnb, t('region.gnb'));
                                }
                                return;
                            } else if (isScreenSaverHomeMenu()) {
                                const wrapped = wrapScreenSaverHomeRegion(context.availableRegions, 'up');
                                if (wrapped) focusAndAnnounce(wrapped, undefined, false);
                                return;
                            } else if (focusAreaAbove('content')) {
                                // vertical: content -> footer (wrap)
                                return;
                            } else if (isHomePage && footer) {
                                focusAndAnnounce(footer, t('region.footer'));
                            } else if (gnb) {
                                focusAndAnnounce(gnb, t('region.gnb'));
                            }
                            return;
                            break;
                        case 'footer':
                            if (context.isInParent) {
                                if (focusAreaAbove('footer')) {
                                    return;
                                }
                                if (isHomePage && gnb) {
                                    focusAndAnnounce(gnb, t('region.gnb'));
                                } else if (mainContent) {
                                    focusAndAnnounce(mainContent, t('region.content'));
                                }
                                return;
                            }
                            if (context.isInRegion) {
                                // Đang ở trong region -> tìm focusable element trong region trước đó
                                const targetElement = navigateToAdjacentRegion(focusedElement, context.availableRegions, 'up');
                                if (targetElement) {
                                    focusAndAnnounce(targetElement, undefined, false);
                                } else if (focusAreaAbove('footer')) {
                                    // vertical: footer -> gnb
                                } else if (isHomePage && gnb) {
                                    focusAndAnnounce(gnb, t('region.gnb'));
                                } else if (mainContent) {
                                    focusAndAnnounce(mainContent, t('region.content'));
                                }
                                return;
                            } else if (focusAreaAbove('footer')) {
                                // vertical: footer -> gnb
                                return;
                            } else if (isHomePage && gnb) {
                                focusAndAnnounce(gnb, t('region.gnb'));
                            } else if (mainContent) {
                                focusAndAnnounce(mainContent, t('region.content'));
                            }
                            return;
                            break;
                        case 'zoom':
                            if (context.isInRegion) {
                                // Đang ở trong region -> tìm focusable element trong region trước đó
                                const targetElement = navigateToAdjacentRegion(focusedElement, context.availableRegions, 'up');
                                if (targetElement) {
                                    focusAndAnnounce(targetElement, undefined, false);
                                } else {
                                    // Không có region trước đó -> chuyển qua footer (other) / content (home)
                                    if (isHomePage && mainContent) {
                                        focusAndAnnounce(mainContent, t('region.content'));
                                    } else if (footer) {
                                        focusAndAnnounce(footer, t('region.footer'));
                                    }
                                }
                                return;
                            } else {
                                // Không ở trong region -> chuyển qua footer (other) / content (home)
                                if (isHomePage && mainContent) {
                                    focusAndAnnounce(mainContent, t('region.content'));
                                } else if (footer) {
                                    focusAndAnnounce(footer, t('region.footer'));
                                }
                                return;
                            }
                            break;
                        default:
                            // Fallback: focus vào content
                            if (mainContent) {
                                focusAndAnnounce(mainContent, t('region.content'));
                            }
                            return;
                    }
                } else {
                    // ArrowDown
                    switch (context.area) {
                        case 'gnb':
                            if (context.isInParent) {
                                if (focusAreaBelow('gnb')) {
                                    return;
                                }
                                if (isHomePage && footer) {
                                    focusAndAnnounce(footer, t('region.footer'));
                                } else if (mainContent) {
                                    focusAndAnnounce(mainContent, t('region.content'));
                                }
                                return;
                            }
                            if (context.isInRegion) {
                                // Đang ở trong region -> tìm focusable element trong region tiếp theo
                                const targetElement = navigateToAdjacentRegion(focusedElement, context.availableRegions, 'down');
                                if (targetElement) {
                                    focusAndAnnounce(targetElement, undefined, false);
                                } else if (focusAreaBelow('gnb')) {
                                    // vertical: gnb -> footer
                                } else {
                                    // Không có region tiếp theo -> chuyển qua footer (home) / content (other)
                                    if (isHomePage && footer) {
                                        focusAndAnnounce(footer, t('region.footer'));
                                    } else if (mainContent) {
                                        focusAndAnnounce(mainContent, t('region.content'));
                                    }
                                }
                                return;
                            } else if (focusAreaBelow('gnb')) {
                                // vertical: gnb -> footer
                                return;
                            } else {
                                // Không ở trong region -> chuyển qua footer (home) / content (other)
                                if (isHomePage && footer) {
                                    focusAndAnnounce(footer, t('region.footer'));
                                } else if (mainContent) {
                                    focusAndAnnounce(mainContent, t('region.content'));
                                }
                                return;
                            }
                            break;
                        case 'content':
                            if (context.isInParent) {
                                if (focusAreaBelow('content')) {
                                    return;
                                }
                                if (isHomePage) {
                                    if (zoom) {
                                        focusAndAnnounce(zoom, t('region.zoom'));
                                    } else if (gnb) {
                                        focusAndAnnounce(gnb, t('region.gnb'));
                                    }
                                } else if (footer) {
                                    focusAndAnnounce(footer, t('region.footer'));
                                }
                                return;
                            }
                            if (context.isInRegion) {
                                // Đang ở trong region -> tìm focusable element trong region tiếp theo
                                const targetElement = navigateToAdjacentRegion(focusedElement, context.availableRegions, 'down');
                                if (targetElement) {
                                    focusAndAnnounce(targetElement, undefined, false);
                                } else if (isScreenSaverHomeMenu()) {
                                    const wrapped = wrapScreenSaverHomeRegion(context.availableRegions, 'down');
                                    if (wrapped) focusAndAnnounce(wrapped, undefined, false);
                                } else {
                                    // Không có region tiếp theo -> chuyển qua zoom/gnb (home) / footer (other)
                                    if (isHomePage) {
                                        if (zoom) {
                                            focusAndAnnounce(zoom, t('region.zoom'));
                                        } else if (gnb) {
                                            focusAndAnnounce(gnb, t('region.gnb'));
                                        }
                                    } else if (focusAreaBelow('content')) {
                                        // vertical: content -> gnb
                                    } else if (footer) {
                                        focusAndAnnounce(footer, t('region.footer'));
                                    }
                                }
                                return;
                            } else if (isScreenSaverHomeMenu()) {
                                const wrapped = wrapScreenSaverHomeRegion(context.availableRegions, 'down');
                                if (wrapped) focusAndAnnounce(wrapped, undefined, false);
                                return;
                            } else if (focusAreaBelow('content')) {
                                // vertical: content -> gnb
                                return;
                            } else {
                                // Không ở trong region -> chuyển qua zoom/gnb (home) / footer (other)
                                if (isHomePage) {
                                    if (zoom) {
                                        focusAndAnnounce(zoom, t('region.zoom'));
                                    } else if (gnb) {
                                        focusAndAnnounce(gnb, t('region.gnb'));
                                    }
                                } else if (footer) {
                                    focusAndAnnounce(footer, t('region.footer'));
                                }
                                return;
                            }
                            break;
                        case 'footer':
                            if (context.isInParent) {
                                if (focusAreaBelow('footer')) {
                                    return;
                                }
                                if (isHomePage && mainContent) {
                                    focusAndAnnounce(mainContent, t('region.content'));
                                } else if (zoom) {
                                    focusAndAnnounce(zoom, t('region.zoom'));
                                } else if (gnb) {
                                    focusAndAnnounce(gnb, t('region.gnb'));
                                }
                                return;
                            }
                            if (context.isInRegion) {
                                // Đang ở trong region -> tìm focusable element trong region tiếp theo
                                const targetElement = navigateToAdjacentRegion(focusedElement, context.availableRegions, 'down');
                                if (targetElement) {
                                    focusAndAnnounce(targetElement, undefined, false);
                                } else if (focusAreaBelow('footer')) {
                                    // vertical: footer -> content (wrap)
                                } else {
                                    // Không có region tiếp theo -> chuyển qua content (home) / zoom hoặc gnb (other)
                                    if (isHomePage && mainContent) {
                                        focusAndAnnounce(mainContent, t('region.content'));
                                    } else if (zoom) {
                                        focusAndAnnounce(zoom, t('region.zoom'));
                                    } else if (gnb) {
                                        focusAndAnnounce(gnb, t('region.gnb'));
                                    }
                                }
                                return;
                            } else if (focusAreaBelow('footer')) {
                                // vertical: footer -> content (wrap)
                                return;
                            } else {
                                // Không ở trong region -> chuyển qua content (home) / zoom hoặc gnb (other)
                                if (isHomePage && mainContent) {
                                    focusAndAnnounce(mainContent, t('region.content'));
                                } else if (zoom) {
                                    focusAndAnnounce(zoom, t('region.zoom'));
                                } else if (gnb) {
                                    focusAndAnnounce(gnb, t('region.gnb'));
                                }
                                return;
                            }
                            break;
                        case 'zoom':
                            if (context.isInRegion) {
                                // Đang ở trong region -> tìm focusable element trong region tiếp theo
                                const targetElement = navigateToAdjacentRegion(focusedElement, context.availableRegions, 'down');
                                if (targetElement) {
                                    focusAndAnnounce(targetElement, undefined, false);
                                } else {
                                    // Không có region tiếp theo -> chuyển qua gnb
                                    if (gnb) {
                                        focusAndAnnounce(gnb, t('region.gnb'));
                                    }
                                }
                                return;
                            } else {
                                // Không ở trong region -> chuyển qua gnb
                                if (gnb) {
                                    focusAndAnnounce(gnb, t('region.gnb'));
                                }
                                return;
                            }
                            break;
                        default:
                            // Fallback: focus vào content
                            if (mainContent) {
                                focusAndAnnounce(mainContent, t('region.content'));
                            }
                            return;
                    }
                }

                if (context.area === 'unknown') {
                    // Only speak if voice is enabled in config
                    if (voice) {
                        speak(t('region.no_region'));
                    }
                }
            };

            document.addEventListener('keydown', handleKeyDown);

            return () => {
                document.removeEventListener('keydown', handleKeyDown);
                observer.disconnect();
            };
        };

        return initRegionNavigation();
    }, [speak, isHorizontal, gnbSelector, mainContentSelector, footerSelector, zoomSelector, regionSelector, voice, t, location.pathname]);
};
